import React from 'react';
import { useFetchData } from 'hooks/useFetchData';
import { AnchorAPI } from 'modules/AnchorAPI';
import { UserVerificationState } from 'modules/AnchorAPI/user/verifyUserEmail';
import { Button } from 'shared/Button/NewButton';
import Icon from 'shared/Icon';
import { paths } from 'client/shared/Paths';
import { useT } from 'client/i18n/hooks/useT';
import serverRenderingUtils from '../../../helpers/serverRenderingUtils';
import styles from './styles.sass';

type Params = {
  code: string;
  userId: number;
};

type Response = {
  userVerificationState: UserVerificationState;
};

type Props = Params & Response;

export function EmailVerifier({
  code,
  userId,
  userVerificationState,
  isForwardToEpisodeList,
}: Props & { isForwardToEpisodeList: boolean }) {
  const t = useT();
  const {
    state: { status },
  } = useFetchData<Params, Response>({
    fetchFunction: AnchorAPI.verifyUserEmail,
    parameters: { code, userId },
  });

  const isMobile =
    serverRenderingUtils.isAndroidChrome() || serverRenderingUtils.isIOS();
  const statusMessageOptions = {
    default: t(
      'VerifyUserEmailScreen.EmailVerifier.StatusMessage.default',
      "Great! Now you can publish an episode whenever you're ready.",
      'The message if the status is unverified or does not exist when verifying an email'
    ),
    error: (
      <>
        {t(
          'VerifyUserEmailScreen.EmailVerifier.StatusMessage.Error',
          'We are unable to verify your email. If you need more help, please reach out to {helpPageLink}',
          'The message if the status is error when verifying an email, which in english with the variable would be: "We are unable to verify your email. If you need more help, please reach out to https://help.anchor.fm/hc/en-us"',
          {
            helpPageLink: (
              <a
                href={paths.getHelpPage('home')}
                key="email-verifier__error-page-help-link"
              >
                {paths.getHelpPage('home')}
              </a>
            ),
          }
        )}
      </>
    ),
    verified: t(
      'VerifyUserEmailScreen.EmailVerifier.StatusMessage.Verified',
      'Great! Your email address has been updated.',
      'The message if the status is verified when verifying an email'
    ),
  };
  const printMessage = () => {
    if (status === 'error') {
      return statusMessageOptions.error;
    }
    switch (userVerificationState) {
      case 'verifiedEmailChangeRequested':
        return statusMessageOptions.verified;
      case 'unverified':
      default:
        return statusMessageOptions.default;
    }
  };
  return (
    <div className={styles.container}>
      {status !== 'error' && (
        <div className={styles.emailIcon}>
          <Icon type="EmailVerified" />
        </div>
      )}
      <h1 className={styles.title}>
        {status !== 'error'
          ? t(
              'VerifyUserEmailScreen.EmailVerifier.Title.Verified',
              'Email verified',
              'The title when an email has been verified on the verify user email screen'
            )
          : t(
              'VerifyUserEmailScreen.EmailVerifier.Title.Unverified',
              "We're having trouble right now",
              'The title when an email has been not been verified on the verify user email screen due to an error'
            )}
      </h1>
      <p className={styles.copy}>{printMessage()}</p>
      {isMobile && (
        <Button
          kind="link"
          color="purple"
          className={styles.button}
          href="AnchorFM://anchor.fm/emailVerification"
        >
          {t(
            'VerifyUserEmailScreen.EmailVerifier.Action.OpenAnchorApp',
            'Open in Anchor',
            'The action button on mobile to open page in Anchor app to verify email on verify user email screen'
          )}
        </Button>
      )}
      <Button
        color="white"
        href={isForwardToEpisodeList ? '/dashboard/episodes' : '/dashboard'}
        kind="link"
      >
        {isForwardToEpisodeList
          ? t(
              'VerifyUserEmailScreen.EmailVerifier.Action.ContinueToEpisodeList',
              'Continue to episode list',
              "The action button to continue on to a user's episode list on verify user email screen"
            )
          : t(
              'VerifyUserEmailScreen.EmailVerifier.Action.ContinueToDashboard',
              'Continue to dashboard',
              "The action button to continue on to a user's dashboard on verify user email screen"
            )}
      </Button>
    </div>
  );
}
